<template>
  <div :class="['text-cont', `${attributes?.layout || 'left'}`]">
    <div class="heading-cont">
      <div
        v-if="attributes?.heading"
        class="heading-text"
        v-html="sanitize(attributes?.heading, cleanStyles)"
      />
      <slot name="heading" />
    </div>
    <div :class="['body-cont', `${hasBodyAndLeftAlign()}`]">
      <slot name="body" />
      <Ctas
        :ctas="ctas"
        :alignment="attributes?.alignment"
        :link-class="`link${attributes?.layout === 'smallHeader' ? '' : '-upper'}-med`"
      />
    </div>
  </div>
</template>

<script setup>
import { sanitize } from '@vue-util/sanitize';
import Ctas from '../ctas';
import cleanStyles from '../../helpers/getCleanStyles';

const props = defineProps({
  attributes: {
    type: Object,
    default: () => {},
  },
  ctas: {
    type: Object,
    default: () => {},
  },
});

const fontColor = props.attributes?.fontColor || 'inherit';

const hasBodyAndLeftAlign = () => (props.attributes?.heading !== '' && props.attributes?.layout === 'left'
  ? 'ctas-padding-top'
  : '');
</script>

<style lang="scss" scoped>
@import '~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss';
.text-cont {
  color: v-bind(fontColor);
  :deep(.heading-text),
  :deep(.body-text) {
    * {
      color: v-bind(fontColor);
    }
  }
  :deep(.body-text) {
    padding-bottom: rem-calc(11);
    @include breakpoint(medium) {
      padding-bottom: 0;
    }
  }
  :deep(.heading-text) {
    padding-bottom: rem-calc(7);
    @include breakpoint(medium) {
      padding-bottom: rem-calc(8);
    }
  }
  &.centerBelowLink,
  &.centerInlineLink,
  &.smallHeader,
  &.center {
    text-align: center;
    padding-right: rem-calc(10%);
    padding-left: rem-calc(10%);
    @include breakpoint(large) {
      padding-right: rem-calc(20%);
      padding-left: rem-calc(20%);
    }
  }
  &.left {
    text-align: left;
    .heading-text, :deep(.body-text) {
      padding-right: rem-calc(10%)
    }
    @include breakpoint(medium) {
      display: flex;
      justify-content: space-between;
      .heading-text, :deep(.body-text) {
        padding-right: rem-calc(20%)
      }
    }
    :deep(.ctas-cont) {
      @media only screen and (max-width:#{map-get($breakpoints, 'medium')-1}) {
        display: flex;
        flex-wrap: wrap;
        row-gap: rem-calc(24px);
        justify-content: left;
        margin-top: 0;
      }
    }
  }
  &.centerBelowLink, &.center {
    :deep(.body-text) {
      @include breakpoint(large) {
        @include padding-direction('bottom', map-get($spacer, 'xs'));
      }
      @include breakpoint(medium) {
        @include padding-direction('bottom', map-get($spacer, 'xs'));
      }
    }
    :deep(.ctas-cont) {
      @media only screen and (max-width:#{map-get($breakpoints, 'medium')-1}) {
        display: flex;
        flex-wrap: wrap;
        row-gap: rem-calc(24px);
        justify-content: center;
        margin-top: 0;
      }
    }
  }
  &.centerInlineLink {
    :deep(.body-text) {
      display: inline;
      margin-right: rem-calc(20);
      p,
      div {
        display: inline;
      }
      + .ctas-cont {
      margin-left: rem-calc(20);
    }
    }
    :deep(.ctas-cont) {
      display: inline;
    }
  }
  &.smallHeader {
    .heading-text > :deep(*) {
      font-size: rem-calc(16);
    }
    :deep(.heading-text) {
      padding-bottom: rem-calc(4);
    }
    :deep(.body-text) {
      padding-bottom: rem-calc(4);
      @include breakpoint(medium) {
        padding-bottom: rem-calc(8);
        }
    }
  }
}

:deep(.ctas-cont) {
  display: inline;
  a {
    &:not(:last-of-type) {
      margin-right: rem-calc(20);
    }
  }
}
</style>

<style lang="scss" brand="mcom" scoped>

.text-cont {
  &.left {
    @include breakpoint(medium) {
      align-items: flex-start;
    }
  }
  .body-cont {
    &.ctas-padding-top {
      padding-top: 10px;
    }
  }
  @include breakpoint(small down) {
    .body-cont.ctas-padding-top {
      padding-top: 0px;
    }
  }
}

</style>

<style lang="scss" brand="bcom" scoped>

.text-cont {
  &.left {
    @include breakpoint(medium) {
      align-items: flex-end;
    }
  }

  .body-cont {
    &.padding-bottom {
      padding-bottom: 11px;
    }
  }
}

</style>
